import getUserInfo from "@api/auth/getUserInfo";
import setNewPassword from "@api/auth/setNewPassword";
import MainForm from "@components/common/MainForm";
import { useUserContext } from "@contexts/UserContext/index.context";
import useLocation from "@hooks/location.hook";
import { Alert } from "@mui/material";
import { revalidateLogic } from "@tanstack/react-form";
import type { FunctionComponent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import validatePassword, { validatePasswordAtOnce } from "@/api/validation/password";
import { useAppForm } from "@/components/Forms";
import { AlbInput } from "@/components/Forms/albInput";
import type { TNewPasswordSchema } from "@/schemas/Auth";
import { NewPasswordSchema } from "@/schemas/Auth";

import { PasswordValidator } from "../PasswordValidator";

type Props = {
  session: string;
  email: string;
};

export const SetNewPassword: FunctionComponent<Props> = ({ session, email }) => {
  const { t } = useTranslation();
  const { handleSetUser } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [_passwordError, setPasswordError] = useState<string>("");
  const [submissionError, setSubmissionError] = useState<string>("");

  const handleRedirect = () => {
    const pathName = location?.from?.pathname || "/";
    navigate(pathName);
  };

  const form = useAppForm({
    defaultValues: {
      email,
      password: "",
      confirmPassword: "",
    } as TNewPasswordSchema,
    validationLogic: revalidateLogic(),
    validators: {
      onChange: NewPasswordSchema,
    },
    onSubmit: async ({ value }) => {
      setPasswordError("");
      setSubmissionError("");

      const validationError = validatePassword(value.password);

      if (!validationError.valid) {
        setPasswordError(validationError.message);
        return;
      }

      try {
        const res = await setNewPassword({
          session,
          username: email,
          newPassword: value.password,
        });

        if (res.success) {
          const { success, data: userData } = await getUserInfo();
          if (success && userData) {
            handleSetUser({ ...userData, connected: true });
          }
          handleRedirect();
        } else {
          setSubmissionError(t("common.error.password-update-failed"));
        }
      } catch (_error) {
        setSubmissionError(t("common.error.unexpected"));
      }
    },
  });

  return (
    <form.AppForm>
      <form.Subscribe
        selector={(s) => ({
          password: s.values.password,
          confirmPassword: s.values.confirmPassword,
        })}
      >
        {({ password, confirmPassword }) => {
          const passwordStr = password ?? "";
          const confirmPasswordStr = confirmPassword ?? "";
          const isValid =
            passwordStr.length >= 8 &&
            passwordStr === confirmPasswordStr &&
            validatePasswordAtOnce(passwordStr).valid === true;

          return (
            <MainForm
              modalForm
              isValid={isValid}
              onSubmit={() => {
                form.handleSubmit();
              }}
              title={t("common.form.input.set-new-password")}
            >
              <form.AppField name="email">
                {() => (
                  <AlbInput
                    label={t("common.form.input.email")}
                    type="TextField"
                    variant="filled"
                    isEditAllowed={false}
                  />
                )}
              </form.AppField>
              <form.AppField name="password">
                {() => (
                  <AlbInput
                    label={t("common.form.input.password")}
                    type="TextField"
                    inputType="password"
                    variant="filled"
                    stripSpaces
                  />
                )}
              </form.AppField>

              <form.AppField name="confirmPassword">
                {() => (
                  <AlbInput
                    label={t("common.form.input.confirm-password")}
                    type="TextField"
                    inputType="password"
                    variant="filled"
                    stripSpaces
                  />
                )}
              </form.AppField>
              {submissionError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submissionError}
                </Alert>
              )}
              <PasswordValidator password={passwordStr} confirmPassword={confirmPasswordStr} />
            </MainForm>
          );
        }}
      </form.Subscribe>
    </form.AppForm>
  );
};
